import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconTrash, IconGripVertical } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import api from '@/services/api-service';
import { notify } from '@/lib/notify';
import { Service } from '@turizi/shared';
import { queryKeys } from '@/hooks/api-hooks';
import { useOutletContext } from 'react-router-dom';
import { Button, FileInput, Loader } from '@mantine/core';

export default function ServiceImagesManager() {
    const service = useOutletContext<Service>();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [isUploading, setIsUploading] = useState(false);
    const [selectedImage, setSelectedImage] = useState<File | null>(null);

    // Mutation for uploading image
    const uploadMutation = useMutation({
        mutationFn: async (file: File) => {
            const formData = new FormData();
            formData.append('image', file);
            return api.upload(`/services/${service.id}/images`, formData);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(service.id.toString()) });
            notify.success(t('success'), t('image_uploaded'));
        },
        onError: (error: any) => {
            notify.error(t('error_uploading_image'), error.message);
        },
        onSettled: () => {
            setSelectedImage(null);
            setIsUploading(false);
        }
    });

    // Mutation for deleting image
    const deleteMutation = useMutation({
        mutationFn: async (imageId: number) => {
            return api.delete(`/services/${service.id}/images`, imageId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(service.id.toString()) });
            notify.success(t('success'), t('image_deleted'));
        },
        onError: (error: any) => {
            notify.error(t('error_deleting_image'), error.message);
        }
    });

    // Mutation for reordering
    const reorderMutation = useMutation({
        mutationFn: async (imageOrders: { id: number, order: number }[]) => {
            return api.put(`/services/${service.id}/images`, 'reorder' as any, { imageOrders });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(service.id.toString()) });
        }
    });

    const uploadImage = async () => {
        setIsUploading(true);
        uploadMutation.mutate(selectedImage!);
    };

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }

        const items = Array.from(service.images || []);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        // Optimistic update could go here, but for now we just call API
        const updates = items.map((img, index) => ({
            id: img.id,
            order: index
        }));

        reorderMutation.mutate(updates);
    };

    const sortedImages = [...(service.images || [])].sort((a, b) => a.order - b.order);

    return (
        <div>

            <div className="flex gap-2 items-center mb-12">
                <FileInput
                    accept="image/*"
                    onChange={setSelectedImage}
                    disabled={isUploading}
                    placeholder={t('select_image')}
                />
                <Button
                    onClick={() => uploadImage()}
                    color='black'
                    radius="xl"
                    loading={isUploading}
                    disabled={isUploading || !selectedImage}
                >
                    {t('upload')}
                </Button>
            </div>

            {
                sortedImages.length === 0 ? (
                    <div className="text-center py-10 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                        <p className="text-gray-500">{t('no_images_yet')}</p>
                    </div>
                ) : (
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="images" direction="horizontal">
                            {(provided) => (
                                <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                    className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4"
                                >
                                    {sortedImages.map((image, index) => (
                                        <Draggable key={image.id} draggableId={image.id.toString()} index={index}>
                                            {(provided) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    className="relative group h-fit w-fit bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                                                >
                                                    <div className="">
                                                        <img
                                                            src={image.url}
                                                            alt="Service"
                                                            className="w-full h-full object-cover"
                                                        />
                                                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <button
                                                                onClick={() => deleteMutation.mutate(image.id)}
                                                                className="bg-red-100 text-red-600 p-1 rounded-full hover:bg-red-200"
                                                                title={t('delete')}
                                                                disabled={deleteMutation.isPending && deleteMutation.variables === image.id}
                                                            >
                                                                {deleteMutation.isPending && deleteMutation.variables === image.id ? (
                                                                    <Loader size="xs" color="red" />
                                                                ) : (
                                                                    <IconTrash size={16} />
                                                                )}
                                                            </button>
                                                        </div>
                                                        <div
                                                            {...provided.dragHandleProps}
                                                            className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity cursor-move bg-white/80 p-1 rounded"
                                                        >
                                                            <IconGripVertical size={16} className="text-gray-600" />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                )
            }
        </div >
    );
}
